import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { APP_VERSION, LOGO } from '@/shared/config';

// The menu entries. Their names are sentence case ("4×6 stickers") so they never read the same as the start page's cards
// ("4 × 6 Sticker"), which name the label types.
const NAV = [
  { to: '/', label: 'All label types', match: ['/', '/index.html'] },
  { to: '/type1-sticker-4x6.html', label: '4×6 stickers', match: ['/type1-sticker-4x6.html'] },
  { to: '/type2-letter-sheets.html', label: 'Letter sheets', match: ['/type2-letter-sheets.html'] },
  { to: '/price-stickers', label: 'Price stickers', match: ['/price-stickers'] },
];

// The Toolkit's frame around every page: the dark green menu on the left (logo, "Label Studio", the label types, a link back
// to the Toolkit), the page itself on the right, and the version in the corner. On a phone the menu folds away behind the
// Menu button and closes again once a page is chosen. Label Studio has no sign-in, so there is no "signed in as" box.
export function Shell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // a new page closes the phone menu (adjusting state while rendering, React's way to follow a changing value)
  const [lastPath, setLastPath] = useState(pathname);
  if (lastPath !== pathname) { setLastPath(pathname); setOpen(false); }

  return (
    <>
      <a className="skip-link" href="#content" onClick={(e) => { e.preventDefault(); document.getElementById('content')?.focus(); }}>Skip to the content</a>
      <aside className={`side${open ? ' open' : ''}`}>
        <div className="side-head">
          <img src={LOGO} alt="Uncle Wiener's Wholesale" width={160} height={60} decoding="async" />
          <div><h1>Label Studio</h1><p>Price labels &amp; shelf tags</p></div>
          <button type="button" className="btn ghost sm menu-btn" aria-controls="nav" aria-expanded={open} onClick={() => setOpen(!open)}>Menu</button>
        </div>
        <nav className="nav" id="nav" aria-label="Main">
          <div className="nav-title">Label types</div>
          {NAV.map((n) => {
            const active = n.match.includes(pathname);
            return <Link key={n.to} to={n.to} className={active ? 'active' : ''} aria-current={active ? 'page' : undefined}>{n.label}</Link>;
          })}
          <div className="nav-title">Other tools</div>
          <a href="https://toolkit.unclewiener.com/" target="_blank" rel="noopener">Toolkit<span className="ext" aria-hidden="true">↗</span><span className="sr-only"> (opens in a new tab)</span></a>
        </nav>
      </aside>
      <main className="main">{children}</main>
      <div className="version" aria-hidden="true">Label Studio {APP_VERSION}</div>
    </>
  );
}
